import {useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {sizeClass} from './sectionMeta';
import styles from './ordersMetrics.module.scss';

/**
 * A collapsible metrics section.
 *
 * Replaces a plain accordion whose collapsed state was seven identical rows —
 * a list of nouns with no figures, so there was nothing to read without
 * opening each one and no reason to open any particular one first.
 *
 * Each header now carries the section's own headline numbers, so the whole
 * panel can be scanned closed, and `tone` lets a section that needs attention
 * (failures, an aging settlement queue) say so from the header.
 */

const Chevron = ({open}) => (
  <svg
    className={`${styles.sectionChevron} ${
      open ? styles.sectionChevronOpen : ''
    }`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/**
 * One headline figure in a section header.
 *
 * `currency` is rendered as its own token rather than glued to the number,
 * because a figure here is always one currency's figure and never a total
 * across them.
 */
export const Highlight = ({label, value, currency, extra, tone}) => (
  <div
    className={`${styles.highlight} ${tone ? styles[`highlight${tone}`] : ''}`}>
    <span className={styles.highlightLabel}>{label}</span>
    <span
      className={`${styles.highlightValue} ${
        styles[`highlightValue${sizeClass(value)}`] ?? ''
      }`}>
      {currency && <span className={styles.highlightCurrency}>{currency}</span>}
      {value}
      {extra && <span className={styles.highlightExtra}>{extra}</span>}
    </span>
  </div>
);

export const SectionCard = ({
  icon,
  title,
  description,
  highlights,
  tone,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section
      className={`${styles.section} ${tone ? styles[`section${tone}`] : ''}`}>
      <button
        type="button"
        className={styles.sectionHeader}
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}>
        <span className={styles.sectionIcon}>{icon}</span>
        <span className={styles.sectionTitleBlock}>
          <span className={styles.sectionTitle}>{title}</span>
          {description && (
            <span className={styles.sectionDescription}>{description}</span>
          )}
        </span>
        <span className={styles.sectionHighlights}>{highlights}</span>
        <Chevron open={open} />
      </button>
      {/* Animated rather than snapped in — the panel used to appear instantly,
          which read as a jump rather than a disclosure. */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className={styles.sectionReveal}
            initial={{height: 0, opacity: 0}}
            animate={{height: 'auto', opacity: 1}}
            exit={{height: 0, opacity: 0}}
            transition={{duration: 0.24, ease: [0.4, 0, 0.2, 1]}}>
            <div className={styles.sectionBody}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default SectionCard;
